import { base64UrlEncodeJson } from "./orderRealtimeSession";

/**
 * AppSync **Events** API 的 WebSocket 客戶端（不用 Amplify）。
 *
 * 注意這跟 DeliveryTracking/appsyncRealtime.ts 是不同的協定：那支連的是 GraphQL API（graphql-ws），
 * 這支連的是 Events API（訂單即時刷新，規格 C7）。兩者看起來很像，差在：
 *
 * | | GraphQL（appsyncRealtime.ts） | Events（本檔） |
 * |---|---|---|
 * | 授權放哪 | URL query `?header=…&payload=…`（標準 base64） | subprotocol `header-{base64url}`，URL 不帶 query |
 * | subprotocol | `graphql-ws` | `aws-appsync-event-ws` + 授權 header |
 * | 端點路徑 | `/graphql` | `/event/realtime` |
 * | 訂閱訊息 | `start` + payload.data（GraphQL 查詢字串） | `subscribe` + `channel` + `authorization` |
 * | 取消訂閱 | `stop` | `unsubscribe` |
 * | 訂閱成功 | `start_ack` | `subscribe_success` |
 * | ack 的逾時 | `payload.connectionTimeoutMs` | 最上層 `connectionTimeoutMs`（保險起見也讀 payload） |
 * | 推送內容 | `payload.data.<欄位>` | `event`（JSON 字串，或 JSON 字串陣列） |
 *
 * 看門狗、closedByUs、WebSocketImpl/makeId 可注入的結構沿用 appsyncRealtime.ts：
 * LINE webview 在行動網路下常出現沒有 close 事件的死連線，只能靠自己計時發現。
 */

type WebSocketCtor = { new (url: string, protocols: string[]): WebSocket; OPEN: number };

export interface EventsSubscriptionOptions {
  /** wss://{realtime domain}/event/realtime */
  wssUrl: string;
  /** {http domain}（不含 https:// 與路徑）；授權 header 的 host 必須是這個，不是 realtime 網域 */
  httpHost: string;
  token: string;
  /** /customer/{customer_id} */
  channel: string;
  /** 收到本訂閱的 data：原樣交出 event（字串或字串陣列），由呼叫端解析 */
  onEvent: (raw: unknown) => void;
  /**
   * 訂閱被接受（本訂閱 id 的 subscribe_success）。
   * 訂閱被拒發生在 connection_ack 之後，所以「連上了」要以這裡為準。
   */
  onSubscribed?: () => void;
  /** 通行證被拒（過期、被竄改、訂閱別人的頻道）。呼叫端應換新通行證重連。 */
  onAuthError?: () => void;
  /** 非主動關閉的斷線（含看門狗逾時）。呼叫端決定要不要重連。 */
  onClose?: () => void;
  WebSocketImpl?: WebSocketCtor;
  makeId?: () => string;
  /** 握手完成前的看門狗逾時；ack 之後改用伺服器給的值。測試用。 */
  connectionTimeoutMs?: number;
}

/**
 * 多久沒收到任何訊息就判定連線已死。
 * AppSync 會在 connection_ack 給 connectionTimeoutMs（通常 5 分鐘），閒置時每 60 秒送一次 ka；
 * 收到 ack 後就改用它給的值。這個常數是握手完成前、或伺服器沒給值時的預設。
 */
const DEFAULT_CONNECTION_TIMEOUT_MS = 300_000;

/** AWS 規定訂閱 id 要符合 /^[a-zA-Z0-9-_+]{1,128}$/；UUID 與下面的退路都符合 */
const defaultId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : // 舊版 LINE webview 沒有 randomUUID；訂閱 id 只需在這條連線內唯一
      `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const positiveNumber = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);

export function openEventsSubscription(opts: EventsSubscriptionOptions): { close(): void } {
  const WS = opts.WebSocketImpl ?? (WebSocket as unknown as WebSocketCtor);
  const authorization = { host: opts.httpHost, Authorization: opts.token };
  // Events API 的授權放在 subprotocol（URL 不帶 query）；subprotocol 不能有 = + /，所以用 base64url
  const ws = new WS(opts.wssUrl, ["aws-appsync-event-ws", `header-${base64UrlEncodeJson(authorization)}`]);
  const subId = (opts.makeId ?? defaultId)();
  let acked = false;
  let closedByUs = false;
  let timeoutMs = opts.connectionTimeoutMs ?? DEFAULT_CONNECTION_TIMEOUT_MS;
  let watchdog: ReturnType<typeof setTimeout> | null = null;

  const clearWatchdog = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = null;
  };

  /**
   * 每收到一則訊息（含 ka）就重新上膛。
   * 行動網路上常見「socket 還開著、對面已經沒聲音」，onclose 不會觸發，重連也就不會啟動。
   * 逾時就自己 close()，走既有的 onClose 重連路徑。
   */
  const armWatchdog = () => {
    clearWatchdog();
    watchdog = setTimeout(() => {
      watchdog = null;
      ws.close();
    }, timeoutMs);
  };

  /** 錯誤訊息是不是衝著這筆訂閱來的（沒帶 id 的視為整條連線的錯誤） */
  const isOurs = (id: unknown): boolean => id === undefined || id === null || id === subId;

  ws.onopen = () => {
    // 握手是 connection_init → connection_ack；ack 之前不能送訂閱
    ws.send(JSON.stringify({ type: "connection_init" }));
    // 連上了但 ack 一直不來也算死連線，所以看門狗從這裡就開始
    armWatchdog();
  };

  ws.onmessage = (ev: MessageEvent) => {
    // 收到任何東西都代表連線還活著，先重新上膛再管內容是什麼
    armWatchdog();
    let msg: any;
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    switch (msg?.type) {
      case "connection_ack": {
        acked = true;
        // Events API 把逾時放在最上層；GraphQL 放在 payload。文件沒保證，兩個都讀，最上層優先
        const serverTimeout = positiveNumber(msg.connectionTimeoutMs) ?? positiveNumber(msg.payload?.connectionTimeoutMs);
        if (serverTimeout !== null) {
          timeoutMs = serverTimeout;
          armWatchdog();
        }
        ws.send(JSON.stringify({ type: "subscribe", id: subId, channel: opts.channel, authorization }));
        return;
      }
      case "subscribe_success":
        if (msg.id === subId) opts.onSubscribed?.();
        return;
      case "data":
        if (msg.id === subId) opts.onEvent(msg.event);
        return;
      case "connection_error":
        opts.onAuthError?.();
        return;
      case "subscribe_error":
      case "error":
        // 訂閱被拒（通行證過期、頻道不是自己的）。別的訂閱 id 的錯誤與我們無關
        if (isOurs(msg.id)) opts.onAuthError?.();
        return;
      case "broadcast_error":
        // 伺服器端推送單一事件失敗，連線本身沒問題：記下來就好，斷線重連反而會漏更多事件
        console.warn("[eventsRealtime] broadcast_error", msg.errors);
        return;
      default:
        // ka（keep-alive）、unsubscribe_success —— ka 的作用是上面那行 armWatchdog，這裡不必再處理
        return;
    }
  };

  ws.onerror = () => {};

  ws.onclose = () => {
    acked = false;
    clearWatchdog();
    if (!closedByUs) opts.onClose?.();
  };

  return {
    close() {
      closedByUs = true;
      clearWatchdog();
      if (acked && ws.readyState === WS.OPEN) {
        ws.send(JSON.stringify({ type: "unsubscribe", id: subId }));
      }
      ws.close();
    },
  };
}
